import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  createCampaign,
  getCampaign,
  updateCampaign,
  type OutboundCampaign,
  type OutboundCampaignCreate,
  type OutboundCampaignPatch,
} from '../api/campaigns.js';
import { listConnectors } from '../api/connectors.js';
import { listTemplates } from '../api/templates.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';
import { MetaTemplatePreview } from '../components/MetaTemplatePreview.js';

interface FormValues {
  name: string;
  description: string;
  connectorId: string;
  templateId: string;
  scheduledAt: string;
  rateLimitPerSecond: string;
}

const emptyValues: FormValues = {
  name: '',
  description: '',
  connectorId: '',
  templateId: '',
  scheduledAt: '',
  rateLimitPerSecond: '',
};

export const campaignFormSchema = z.object({
  name: z.string().trim().min(1, { message: 'NAME_REQUIRED' }).max(120, { message: 'NAME_TOO_LONG' }),
  description: z.string().max(500, { message: 'DESCRIPTION_TOO_LONG' }).optional(),
  connectorId: z.string().min(1, { message: 'CONNECTOR_REQUIRED' }),
  templateId: z.string().min(1, { message: 'TEMPLATE_REQUIRED' }),
  scheduledAt: z.string().optional(),
  rateLimitPerSecond: z
    .number()
    .int()
    .min(1, { message: 'RATE_LIMIT_INVALID' })
    .max(100, { message: 'RATE_LIMIT_INVALID' })
    .optional(),
});

function toIsoOrUndef(local: string): string | undefined {
  if (!local) return undefined;
  // datetime-local → ISO UTC
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function isoToLocal(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // YYYY-MM-DDTHH:mm in local timezone
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function valuesToCreate(v: FormValues): OutboundCampaignCreate {
  const out: OutboundCampaignCreate = {
    name: v.name.trim(),
    connectorId: v.connectorId,
    templateId: v.templateId,
  };
  if (v.description.trim()) out.description = v.description.trim();
  const iso = toIsoOrUndef(v.scheduledAt);
  if (iso) out.scheduledAt = iso;
  const rl = v.rateLimitPerSecond ? Number(v.rateLimitPerSecond) : undefined;
  if (rl !== undefined && !Number.isNaN(rl)) out.rateLimitPerSecond = rl;
  return out;
}

function buildPatch(
  current: OutboundCampaign,
  v: FormValues,
  variableMap: Record<string, string>,
): OutboundCampaignPatch {
  const patch: OutboundCampaignPatch = {};
  const trimmedName = v.name.trim();
  if (trimmedName !== current.name) patch.name = trimmedName;
  const trimmedDesc = v.description.trim() || undefined;
  if (trimmedDesc !== (current.description ?? undefined)) {
    patch.description = trimmedDesc;
  }
  const iso = toIsoOrUndef(v.scheduledAt);
  if (iso !== (current.scheduledAt ?? undefined)) patch.scheduledAt = iso;
  const rl = v.rateLimitPerSecond ? Number(v.rateLimitPerSecond) : undefined;
  if (rl !== (current.rateLimitPerSecond ?? undefined)) {
    patch.rateLimitPerSecond = rl;
  }
  if (JSON.stringify(variableMap) !== JSON.stringify(current.variableMap ?? {})) {
    patch.variableMap = variableMap;
  }
  return patch;
}

export interface CampaignFormProps {
  mode: 'create' | 'edit';
}

export function CampaignForm({ mode }: CampaignFormProps): JSX.Element {
  const { id = '', campaignId = '' } = useParams<{
    id: string;
    campaignId?: string;
  }>();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();
  const [variableMap, setVariableMap] = useState<Array<[string, string]>>([]);

  const connectorsQuery = useQuery({
    queryKey: ['connectors', id, { includeArchived: false }],
    queryFn: () => listConnectors(id),
    enabled: Boolean(id),
  });

  const templatesQuery = useQuery({
    queryKey: ['templates', id, { includeArchived: false }],
    queryFn: () => listTemplates(id),
    enabled: Boolean(id),
  });

  const campaignQuery = useQuery({
    queryKey: ['campaign', id, campaignId],
    queryFn: () => getCampaign(id, campaignId),
    enabled: mode === 'edit' && Boolean(id && campaignId),
  });

  const defaultValues = useMemo<FormValues>(() => {
    if (mode === 'edit' && campaignQuery.data) {
      const c = campaignQuery.data;
      return {
        name: c.name,
        description: c.description ?? '',
        connectorId: c.connectorId,
        templateId: c.templateId,
        scheduledAt: isoToLocal(c.scheduledAt),
        rateLimitPerSecond: c.rateLimitPerSecond?.toString() ?? '',
      };
    }
    return emptyValues;
  }, [mode, campaignQuery.data]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
    if (mode === 'edit' && campaignQuery.data?.variableMap) {
      setVariableMap(Object.entries(campaignQuery.data.variableMap));
    }
  }, [defaultValues, reset, mode, campaignQuery.data]);

  const watchedConnectorId = watch('connectorId');
  const watchedTemplateId = watch('templateId');

  const templates = templatesQuery.data ?? [];
  const filteredTemplates = watchedConnectorId
    ? templates.filter((tpl) => tpl.connectorId === watchedConnectorId)
    : [];

  // #95: template seleccionado para preview + auto-detección de placeholders.
  const selectedTemplate = templates.find((tpl) => tpl.id === watchedTemplateId);

  // Auto-populate variableMap con placeholders del template al seleccionarlo
  // (no sobreescribe entries que el admin ya editó).
  useEffect(() => {
    if (!selectedTemplate?.placeholders?.length) return;
    setVariableMap((current) => {
      const knownKeys = new Set(current.map(([k]) => k));
      const additions: Array<[string, string]> = selectedTemplate.placeholders
        .filter((p) => !knownKeys.has(p))
        .map((p) => [p, ''] as [string, string]);
      if (additions.length === 0) return current;
      return [...current, ...additions];
    });
  }, [selectedTemplate?.id, selectedTemplate?.placeholders]);

  // Cuando cambia connectorId en create, limpiar templateId si no pertenece.
  useEffect(() => {
    if (mode !== 'create') return;
    if (
      watchedTemplateId &&
      !filteredTemplates.some((tpl) => tpl.id === watchedTemplateId)
    ) {
      setValue('templateId', '');
    }
  }, [mode, watchedConnectorId, watchedTemplateId, filteredTemplates, setValue]);

  const createMutation = useMutation({
    mutationFn: (body: OutboundCampaignCreate) => createCampaign(id, body),
    onSuccess: (created) => {
      pushToast('success', t.campaign.created);
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
      navigate(`/tenants/${id}/campaigns/${created.id}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 422) {
        if (e.code === 'INVALID_CONNECTOR') {
          pushToast('error', t.campaign.invalidConnector);
          return;
        }
        if (e.code === 'INVALID_TEMPLATE') {
          pushToast('error', t.campaign.invalidTemplate);
          return;
        }
        if (e.code === 'TEMPLATE_CONNECTOR_MISMATCH') {
          pushToast('error', t.campaign.templateConnectorMismatch);
          return;
        }
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: OutboundCampaignPatch) => {
      const current = campaignQuery.data;
      if (!current) throw new Error('campaign_not_loaded');
      return updateCampaign(id, current.id, patch, current.version);
    },
    onSuccess: () => {
      pushToast('success', t.campaign.updated);
      queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id, campaignId] });
      navigate(`/tenants/${id}/campaigns/${campaignId}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 412) {
        pushToast('error', t.campaign.versionConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const onSubmit = handleSubmit((values) => {
    const rl = values.rateLimitPerSecond
      ? Number(values.rateLimitPerSecond)
      : undefined;
    const parsed = campaignFormSchema.safeParse({
      ...values,
      rateLimitPerSecond: rl,
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const code = issue?.message ?? 'invalid';
      const friendly: Record<string, string> = {
        NAME_REQUIRED: t.campaign.nameRequired,
        NAME_TOO_LONG: t.campaign.nameTooLong,
        DESCRIPTION_TOO_LONG: t.campaign.descriptionTooLong,
        CONNECTOR_REQUIRED: t.campaign.connectorRequired,
        TEMPLATE_REQUIRED: t.campaign.templateRequired,
        RATE_LIMIT_INVALID: t.campaign.rateLimitInvalid,
      };
      pushToast('error', friendly[code] ?? code);
      return;
    }
    if (mode === 'create') {
      createMutation.mutate(valuesToCreate(values));
    } else if (campaignQuery.data) {
      const vmap: Record<string, string> = {};
      for (const [k, v] of variableMap) {
        if (k.trim()) vmap[k.trim()] = v;
      }
      updateMutation.mutate(buildPatch(campaignQuery.data, values, vmap));
    }
  });

  if (mode === 'edit' && campaignQuery.isLoading) return <p>{t.app.loading}</p>;
  if (mode === 'edit' && campaignQuery.error) {
    return <p className="text-red-700">{(campaignQuery.error as Error).message}</p>;
  }

  const submitting =
    isSubmitting || createMutation.isPending || updateMutation.isPending;
  const connectors = connectorsQuery.data ?? [];
  const current = campaignQuery.data;

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">
        {mode === 'create' ? t.campaign.newTitle : t.campaign.editTitle}
      </h2>
      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded border border-slate-200 bg-white p-5"
        noValidate
      >
        <Field label={t.campaign.fields.name} error={errors.name?.message}>
          <input
            type="text"
            {...register('name', {
              required: t.campaign.nameRequired,
              maxLength: { value: 120, message: t.campaign.nameTooLong },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field
          label={t.campaign.fields.description}
          error={errors.description?.message}
          helper={t.campaign.descriptionHelper}
        >
          <textarea
            rows={3}
            maxLength={500}
            {...register('description', {
              maxLength: { value: 500, message: t.campaign.descriptionTooLong },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field
          label={t.campaign.fields.connector}
          error={errors.connectorId?.message}
        >
          <select
            {...register('connectorId', {
              required: t.campaign.connectorRequired,
            })}
            disabled={mode === 'edit'}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm disabled:bg-slate-100"
          >
            <option value="">—</option>
            {connectors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.provider}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t.campaign.fields.template}
          error={errors.templateId?.message}
        >
          <select
            {...register('templateId', {
              required: t.campaign.templateRequired,
            })}
            disabled={mode === 'edit' || !watchedConnectorId}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm disabled:bg-slate-100"
          >
            <option value="">—</option>
            {filteredTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name} — {tpl.language}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t.campaign.fields.scheduledAt}
          error={errors.scheduledAt?.message}
        >
          <input
            type="datetime-local"
            {...register('scheduledAt')}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field
          label={t.campaign.fields.rateLimitPerSecond}
          error={errors.rateLimitPerSecond?.message}
          helper={t.campaign.rateLimitHelper}
        >
          <input
            type="number"
            min={1}
            max={100}
            {...register('rateLimitPerSecond')}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        {/* #95: preview Meta template con variables interpoladas */}
        {selectedTemplate && (
          <MetaTemplatePreview
            bodyText={selectedTemplate.bodyText}
            variableMap={variableMap}
            declaredPlaceholders={selectedTemplate.placeholders}
          />
        )}

        {mode === 'edit' && current && (
          <>
            <div className="rounded bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <strong>{t.campaign.fields.contactsCount}:</strong>{' '}
              {current.contactsCount}
            </div>
          </>
        )}

        {/* #95: variableMap disponible también en create (no solo edit) */}
        {(mode === 'edit' || selectedTemplate) && (
          <>
            <div>
              <span className="mb-1 block text-sm font-medium text-slate-700">
                {t.campaign.fields.variableMap}
              </span>
              <div className="space-y-2" data-testid="variable-map">
                {variableMap.map(([k, v], idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      aria-label={`${t.campaign.variableKey} ${idx + 1}`}
                      value={k}
                      onChange={(e) => {
                        const next = [...variableMap];
                        next[idx] = [e.target.value, v];
                        setVariableMap(next);
                      }}
                      placeholder={t.campaign.variableKey}
                      className="w-1/3 rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                    />
                    <input
                      type="text"
                      aria-label={`${t.campaign.variableValue} ${idx + 1}`}
                      value={v}
                      onChange={(e) => {
                        const next = [...variableMap];
                        next[idx] = [k, e.target.value];
                        setVariableMap(next);
                      }}
                      placeholder={t.campaign.variableValue}
                      className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                    />
                    <button
                      type="button"
                      aria-label={`remove variable ${idx + 1}`}
                      onClick={() =>
                        setVariableMap(variableMap.filter((_, i) => i !== idx))
                      }
                      className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setVariableMap([...variableMap, ['', '']])}
                  className="rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-50"
                >
                  + {t.campaign.addVariable}
                </button>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Link
            to={`/tenants/${id}/campaigns`}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.app.cancel}
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {submitting ? '…' : t.app.save}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  helper,
  children,
}: {
  label: string;
  error?: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper && !error && (
        <span className="mt-1 block text-xs text-slate-500">{helper}</span>
      )}
      {error && (
        <span role="alert" className="mt-1 block text-xs text-red-600">
          {error}
        </span>
      )}
    </label>
  );
}
